import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useNavigate } from '../lib/router'
import { tokens } from '../theme'

interface Guard {
  /** Describe the work in progress, or null when there is none to lose. */
  block: (reason: string | null) => void
  /**
   * Navigate, asking first if something would be lost. A function is a way
   * out the host provides, such as its own dashboard.
   */
  go: (to: string | (() => void)) => void
  /** Navigate without asking: the screen has already dealt with its work. */
  leave: (to: string | (() => void)) => void
  blocked: boolean
}

const GuardContext = createContext<Guard>({ block: () => {}, go: () => {}, leave: () => {}, blocked: false })

/**
 * Holds whatever unsaved work the current screen has, and asks before leaving it.
 *
 * `BrowserRouter` has no data router, so `useBlocker` is unavailable; instead
 * every in-app link that leaves a screen goes through `go`, and `beforeunload`
 * covers closing or reloading the tab.
 */
export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [reason, setReason] = useState<string | null>(null)
  // Held in an object so a function destination isn't taken for an updater.
  const [pending, setPending] = useState<{ to: string | (() => void) } | null>(null)
  const leave = useCallback(
    (to: string | (() => void)) => (typeof to === 'function' ? to() : navigate(to)),
    [navigate],
  )

  useEffect(() => {
    if (!reason) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [reason])

  const block = useCallback((next: string | null) => setReason(next), [])

  const go = useCallback(
    (to: string | (() => void)) => {
      if (reason) setPending({ to })
      else leave(to)
    },
    [reason, leave],
  )

  const leaveNow = useCallback(
    (to: string | (() => void)) => {
      setReason(null)
      leave(to)
    },
    [leave],
  )

  const value = useMemo(
    () => ({ block, go, leave: leaveNow, blocked: Boolean(reason) }),
    [block, go, leaveNow, reason],
  )

  return (
    <GuardContext.Provider value={value}>
      {children}
      {pending && reason && (
        <Box
          onClick={() => setPending(null)}
          sx={{
            position: 'fixed', inset: 0, zIndex: 40, display: 'grid', placeItems: 'center',
            backgroundColor: 'rgba(17,38,36,.25)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Leave without saving"
            sx={{
              width: 420, p: 6, backgroundColor: 'background.paper',
              borderRadius: `${tokens.radius.lg}px`, boxShadow: tokens.shadow.float,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>Leave without saving?</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6 }}>{reason}</Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setPending(null)}>Stay here</Button>
              <Button
                sx={{ backgroundColor: tokens.color.danger, '&:hover': { backgroundColor: '#8e3b28' } }}
                onClick={() => {
                  const { to } = pending
                  setPending(null)
                  setReason(null)
                  leave(to)
                }}
              >
                Leave
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </GuardContext.Provider>
  )
}

export const useNavigationGuard = () => useContext(GuardContext)

/** Registers unsaved work for as long as `active` holds. */
export function useUnsavedWork(active: boolean, reason: string) {
  const { block } = useNavigationGuard()
  useEffect(() => {
    block(active ? reason : null)
    return () => block(null)
  }, [active, reason, block])
}
